import { supabase } from '@/lib/supabase';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Calls our own API, attaching the current Supabase access token.
 *
 * The server re-validates that token and re-reads the role from the database
 * on every request — it never trusts a claim the browser sends.
 */
export async function apiFetch<T>(
  path: string,
  init: { method?: string; body?: unknown } = {},
): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;

  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method: init.method ?? 'GET',
      headers: {
        /*
         * Only when there IS a body. Fastify rejects a request that declares
         * application/json and then sends nothing —
         * FST_ERR_CTP_EMPTY_JSON_BODY, "Body cannot be empty when content-type
         * is set to 'application/json'" — and several endpoints here are
         * bodyless POSTs by design: /confirm, /accept, /complete. They all
         * failed this way, and the message points at content types rather than
         * at the missing body, so it reads like a server bug.
         */
        ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    });
  } catch {
    throw new ApiError(`Cannot reach the server. Is it running?`, 'network', 0);
  }

  const payload: unknown = await res.json().catch(() => null);

  if (!res.ok) {
    const p = payload as { code?: string; message?: string } | null;

    // A 404 on an /api/ path almost always means the API is running without
    // database credentials, not that the endpoint was never written. Say so,
    // rather than letting someone search the router for a route that is there.
    if (res.status === 404 && path.startsWith('/api/')) {
      throw new ApiError(
        `The server has no ${path} endpoint. Usually this means the API is running ` +
          'without database credentials — check .env and restart pnpm dev.',
        'not_configured',
        404,
      );
    }

    throw new ApiError(p?.message ?? 'Something went wrong.', p?.code ?? 'unknown', res.status);
  }

  return payload as T;
}
